import { isNothing, toMaybe } from "./maybe";
import { invoke } from "@tauri-apps/api/core";
import { open } from "@tauri-apps/plugin-dialog";
import { openDialogDefaults } from "./dialogDefaults";
import { resolveHomeDir } from "./tauri/dialogHome";
import { authorizedFetch } from "./cloudAuth";
import { isTauri } from "./runtime";

export type DesktopLicenseInfo = {
  email: string;
  product: string;
  licenseId: string;
  issuedAt: string;
};

export async function getInstalledDesktopLicense(): Promise<DesktopLicenseInfo | null> {
  if (isTauri() === false) return null;
  return invoke<DesktopLicenseInfo | null>("get_desktop_license");
}

export async function installDesktopLicenseBlob(
  blob: string,
): Promise<DesktopLicenseInfo> {
  if (isTauri() === false) {
    throw new Error("Desktop license activation requires the Zephyrwrite app.");
  }
  return invoke<DesktopLicenseInfo>("install_desktop_license", { blob });
}

export async function clearDesktopLicense(): Promise<void> {
  if (isTauri() === false) return;
  await invoke("clear_desktop_license");
}

export async function isDesktopLicenseActive(): Promise<boolean> {
  if (isTauri() === false) return false;
  return invoke<boolean>("desktop_license_active");
}

/** After sign-in: pull signed blob from cloud and install quietly. */
export async function syncDesktopLicenseFromCloud(): Promise<DesktopLicenseInfo | null> {
  if (isTauri() === false) return null;
  const res = await authorizedFetch("/v1/me/desktop-license");
  if (res.status === 404 || res.status === 403) return null;
  if (res.ok === false) {
    throw new Error(`Could not download your license (${res.status}).`);
  }
  const body = (await res.json()) as { license?: string };
  const licenseM = toMaybe(body.license);
  if (isNothing(licenseM) || licenseM.value.length === 0) return null;
  return installDesktopLicenseBlob(licenseM.value);
}

/** Open a .license file from disk and install it. */
export async function activateDesktopLicenseFromFile(): Promise<DesktopLicenseInfo> {
  if (isTauri() === false) {
    throw new Error("Desktop license activation requires the Zephyrwrite app.");
  }
  const home = await resolveHomeDir();
  const selected = await open({
    ...openDialogDefaults(home),
    multiple: false,
    filters: [{ name: "Zephyrwrite license", extensions: ["license", "txt"] }],
  });
  if (
    selected === null ||
    selected.length === 0 ||
    typeof selected !== "string"
  ) {
    throw new Error("No license file selected.");
  }
  return invoke<DesktopLicenseInfo>("install_desktop_license_from_path", {
    path: selected,
  });
}

export async function activateDesktopLicenseFromPaste(
  blob: string,
): Promise<DesktopLicenseInfo> {
  return installDesktopLicenseBlob(blob);
}
